'use client';

import React from 'react';
import { Button, Input, Link, Checkbox } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router';
import RightImage from '../sign-in/imagecreator.jpg';
import { AcmeIcon } from '../sign-in/acme';
import { parseApiErrorWithFields } from '../../utils/errorParser';
import './index.css';
import { AlertBlock, Typography } from '../../components';
import { LogoCreatorAuthIc } from '../../components/icons';
import { useTranslations } from '../../contexts/translation.context';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

// Password strength calculation
const calculatePasswordStrength = (password: string) => {
  let strength = 0;
  if (/[a-z]/.test(password)) strength++; // lowercase letter
  if (/[A-Z]/.test(password)) strength++; // uppercase letter
  if (/[0-9]/.test(password)) strength++; // digit
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++; // symbol
  return strength;
};

// Get password strength class based on overall complexity
const getPasswordStrengthClass = (strengthLevel: number) => {
  if (strengthLevel >= 4) return 'bg-auth-strength-strong'; // strong (all 4 criteria)
  if (strengthLevel >= 3) return 'bg-auth-strength-good'; // good (3 criteria)
  if (strengthLevel >= 2) return 'bg-auth-strength-medium'; // medium (2 criteria)
  if (strengthLevel >= 1) return 'bg-auth-strength-weak'; // weak (1 criteria)
  return 'bg-auth-strength-none'; // none
};

export default function SignUp() {
  const { t, lang } = useTranslations();
  const [currentStep, setCurrentStep] = React.useState<'select' | 'form'>('select');
  const [userType, setUserType] = React.useState<'marketer' | 'agency' | 'creator'>('agency');
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Новый код для поддержки регистрации по токену ---
  const [inviteToken, setInviteToken] = React.useState<string | null>(null);
  const [inviteName, setInviteName] = React.useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = React.useState<string | null>(null);
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const name = params.get('name');
    const email = params.get('email');
    if (token) setInviteToken(token);
    if (name) setInviteName(name);
    if (email) setInviteEmail(email);
    // Если есть токен, сразу переходим к форме регистрации
    if (token) {
      setCurrentStep('form');
      setUserType('agency'); // По умолчанию agency
    }
  }, [location.search]);

  // Check for skip select from partner navigation
  React.useEffect(() => {
    if (location.state?.skipSelect && location.state?.userType) {
      setUserType(location.state.userType);
      setCurrentStep('form');
      // Clear the state to avoid issues on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

  // Исправленный переход: сохраняем query-параметры
  const handleCreatorAgencySelect = () => {
    setUserType('agency');
    setCurrentStep('form');
    // Не трогаем location/search, чтобы не терять параметры
  };

  const handleMarketerSelect = () => {
    // Redirect to partner sign-up for marketers, сохраняем search
    navigate({ pathname: '/partner/sign-up', search: location.search });
  };

  // Исправленный переход: сохраняем query-параметры
  const handleBackToSelect = () => {
    setCurrentStep('select');
    setError(null);
    // Не трогаем location/search
  };

  const getInitialValues = () => {
    const baseValues: any = {
      firstName: inviteName || '',
      email: inviteEmail || '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    };
    if (userType === 'marketer') {
      baseValues.telegram = '';
    }
    return baseValues;
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    if (!values.acceptTerms) {
      setSubmitAttempted(true);
      setSubmitting(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setSubmitAttempted(false);
      if (inviteToken) {
        // Регистрация через googleAuthRegister с токеном
        const { default: clientAuthService } = await import('../../api/client/clientAuthService');
        const { setClientToken } = await import('../../api/apiClient');
        const response = await clientAuthService.googleAuthRegister(
          {
            ...values,
            type: userType,
            name: values.firstName,
          },
          inviteToken
        );
        // Если есть access_token и refresh_token, авторизуем юзера сразу
        if (response?.data?.access_token) {
          const accessToken = `Bearer ${response.data.access_token}`;
          localStorage.setItem('authToken', accessToken);
          if (response.data.refresh_token) {
            localStorage.setItem('refreshToken', response.data.refresh_token);
          }
          localStorage.setItem('userRole', 'client');
          setClientToken(accessToken);
          // Получаем профиль и авторизуем
          const me = await clientAuthService.getMe({});
          // setUser из AuthContext
          if (typeof window !== 'undefined') {
            const { useAuth } = await import('../../contexts/AuthContext');
            try {
              // useAuth() нельзя вызвать вне компонента, поэтому делаем window.location
              window.location.replace('/client/dashboard');
              return;
            } catch {}
          }
        }
      } else {
        await signup(
          {
            ...values,
            type: userType,
            name: values.firstName,
          },
          'client'
        );
      }
      navigate('/client/sign-in', {
        state: {
          skipSelect: true,
          message: t('auth.signupSuccessMessage'),
          from: location.state?.from,
          userType: userType,
        },
      });
    } catch (error: any) {
      console.error('Registration failed:', error);
      setError(parseApiErrorWithFields(error, t('auth.errors.registrationError')));
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // Динамическая схема: если inviteToken есть и нет inviteEmail, email не обязателен и не показывается
  const createSignUpSchemaPatched = (userType: 'agency' | 'creator' | 'marketer') => {
    const fieldName = userType === 'agency' ? t('auth.agencyName') : t('auth.name');
    const fieldMinMessage = t('auth.validation.min2Chars');
    const fieldRequiredMessage = t('auth.validation.required');
    const baseSchema: any = {
      firstName: Yup.string()
        .min(2, `${fieldName} ${fieldMinMessage}`)
        .required(`${fieldName} ${fieldRequiredMessage}`),
      password: Yup.string()
        .min(8, t('auth.validation.passwordRequirements'))
        .max(25, t('auth.validation.passwordRequirements'))
        .matches(/[a-z]/, t('auth.validation.passwordRequirements')) // lowercase letter
        .matches(/[A-Z]/, t('auth.validation.passwordRequirements')) // uppercase letter
        .matches(/[0-9]/, t('auth.validation.passwordRequirements')) // digit
        .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, t('auth.validation.passwordRequirements')) // symbol
        .required(t('auth.validation.passwordRequired')),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], t('auth.validation.passwordsMatch'))
        .required(t('auth.validation.confirmPasswordRequired')),
      acceptTerms: Yup.boolean(),
    };
    if (!(inviteToken && !inviteEmail)) {
      baseSchema.email = Yup.string()
        .email(t('auth.validation.emailInvalid'))
        .required(t('auth.validation.emailRequired'));
    }
    if (userType === 'marketer') {
      baseSchema.telegram = Yup.string()
        .min(2, t('auth.validation.telegramMin'))
        .required(t('auth.validation.telegramRequired'));
    }
    return Yup.object().shape(baseSchema);
  };

  // User Type Selection Step
  if (currentStep === 'select') {
    return (
      <div className="relative min-h-screen w-full bg-white">
        <div className="absolute top-12 left-12 hidden items-center md:flex">
          <LogoCreatorAuthIc />
        </div>

        <div className="absolute top-12 right-12 hidden md:block">
          <LanguageSwitcher variant="ghost" />
        </div>

        <div className="container flex min-h-screen w-full flex-col pt-[5.25rem] pb-4 md:py-6">
          <div className="md:hidden flex flex-col justify-end fixed top-0 left-0 w-full z-50 bg-white border-b border-solid border-neutral-12 px-4 py-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] shrink-0">
                <LogoCreatorAuthIc className="size-full" />
              </div>
              <LanguageSwitcher variant="ghost" />
            </div>
          </div>

          <div className="flex w-full flex-1 flex-col justify-start md:min-h-[calc(100vh-48px)] md:flex-row md:items-stretch md:justify-center">
            <div className="flex w-full flex-col items-center bg-background py-6 md:justify-center md:px-6 md:py-12">
              <div className="flex w-full flex-col items-center gap-8 md:gap-6">
                <div className="w-full max-w-[26.5rem] md:hidden">
                  <div
                    className="h-[8.75rem] md:h-[12.5rem] w-full overflow-hidden rounded-2xl md:rounded-3xl bg-cover bg-top md:bg-center"
                    style={{
                      backgroundImage: `url(${RightImage})`,
                    }}
                  />
                </div>

                <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-4">
                  <div className="flex w-full flex-col gap-1 md:gap-4 text-center">
                    <Typography variant="h3">{t('auth.signUp')}</Typography>
                    <Typography variant="body1" className="text-auth-muted">
                      {t('auth.selectAccountType')}
                    </Typography>
                  </div>

                  <div className="mt-2 md:mt-4 flex w-full flex-col gap-2">
                    <div
                      onClick={() =>
                        navigate('/client/sign-up', {
                          state: { userType: 'creator', skipSelect: true },
                        })
                      }
                      className="user-type-card w-full cursor-pointer rounded-lg bg-auth-surface p-4 py-5 transition-colors hover:bg-auth-surface-hover"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
                          <Icon
                            width={36}
                            height={36}
                            icon="solar:user-rounded-linear"
                            className="text-auth-ink size-full"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Typography variant="h7">
                            {t('sidebar.creator')} / {t('sidebar.agency')}
                          </Typography>
                          <Typography variant="body2" className="text-auth-muted">
                            {t('auth.descriptions.creator')}
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <div
                      onClick={handleMarketerSelect}
                      className="user-type-card w-full cursor-pointer rounded-lg bg-auth-surface p-4 py-5 transition-colors hover:bg-auth-surface-hover"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
                          <Icon
                            width={36}
                            height={36}
                            icon="solar:user-id-outline"
                            className="text-auth-ink size-full"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Typography variant="h7">{t('sidebar.marketer')}</Typography>
                          <Typography variant="body2" className="text-auth-muted">
                            {t('auth.descriptions.marketer')}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 md:mt-4 flex w-full justify-between">
                    <Typography variant="body2" className="text-auth-muted">
                      {t('auth.haveAccount')}&nbsp;
                    </Typography>
                    <Link
                      href="/client/sign-in"
                      size="sm"
                      className="text-sm leading-[1.125rem] text-partner"
                    >
                      {t('auth.signIn')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration Form Step
  return (
    <div className="relative min-h-screen w-full bg-white">
      <div className="absolute top-12 left-12 hidden items-center md:flex">
        <LogoCreatorAuthIc />
      </div>

      <div className="container flex min-h-screen w-full flex-col pt-[5.25rem] pb-4 md:py-6">
        <div className="md:hidden flex flex-col justify-end fixed top-0 left-0 w-full z-50 bg-white border-b border-solid border-neutral-12 px-4 py-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] shrink-0">
              <LogoCreatorAuthIc className="size-full" />
            </div>
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>

        <div className="flex w-full flex-1 flex-col justify-start md:min-h-[calc(100vh-48px)] md:flex-row md:items-stretch md:justify-center">
          <div className="flex w-full flex-col items-center bg-background py-6 md:flex-1 md:justify-center md:px-6 md:py-12">
            <div className="flex w-full flex-col items-center gap-8 md:gap-6">
              <div className="w-full max-w-[26.5rem] md:hidden">
                <div
                  className="h-[8.75rem] md:h-[12.5rem] w-full overflow-hidden rounded-2xl md:rounded-3xl bg-cover bg-top md:bg-center"
                  style={{
                    backgroundImage: `url(${RightImage})`,
                  }}
                />
              </div>

              <div className="flex w-full max-w-[26.5rem] flex-col items-center">
                {/* Title */}
                <div className="w-full text-center flex flex-col gap-1 md:gap-4 mb-6">
                  <Typography variant="h3">{t('auth.signUp')}</Typography>
                  <Typography variant="body1" className="text-auth-muted">
                    {t('auth.selectAccountType')}
                  </Typography>
                </div>

                {/* User Type Tabs (only for Agency/Creator) */}
                {userType !== 'marketer' && (
                  <div className="w-full mb-6 md:mb-8">
                    <div className="flex h-10 w-full rounded-xl bg-auth-surface p-1">
                      <button
                        type="button"
                        onClick={() => setUserType('agency')}
                        className={`flex-1 h-8 rounded-lg text-sm font-normal transition-colors ${
                          userType === 'agency' ? 'bg-white text-black' : 'text-auth-muted'
                        }`}
                      >
                        {t('sidebar.agency')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setUserType('creator')}
                        className={`flex-1 h-8 rounded-lg text-sm font-normal transition-colors ${
                          userType === 'creator' ? 'bg-white text-black' : 'text-auth-muted'
                        }`}
                      >
                        {t('sidebar.creator')}
                      </button>
                    </div>
                  </div>
                )}

                {error && (
                  // <div className="w-full mb-4 bg-auth-error-bg border-2 border-auth-error-border rounded-xl p-4 shadow-sm">
                  //   <div className="flex items-center gap-4">
                  //     <div className="size-10 bg-auth-error-bg border border-auth-error-border rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                  //       <Icon
                  //         icon="mingcute:information-fill"
                  //         width="19"
                  //         height="19"
                  //         className="text-auth-error-text"
                  //       />
                  //     </div>
                  //     <p className="text-auth-error-text text-sm font-medium leading-5">
                  //       {error}
                  //     </p>
                  //   </div>
                  // </div>
                  <AlertBlock
                    className="mb-4 w-full"
                    type="rounded"
                    data={[{ severity: 'error', description: error }]}
                  />
                )}

                <Formik
                  key={String(!!inviteToken) + String(!!inviteEmail)} // Re-init form only if token/email меняются, not userType
                  initialValues={getInitialValues()}
                  validationSchema={createSignUpSchemaPatched(userType)}
                  onSubmit={handleSubmit}
                  onInvalidSubmit={(values: any) => {
                    if (!values.acceptTerms) {
                      setSubmitAttempted(true);
                    }
                  }}
                >
                  {({ handleSubmit, isSubmitting, values, setFieldValue, errors, isValid }) => (
                    <form
                      className="flex w-full flex-col gap-4"
                      onSubmit={(e) => {
                        handleSubmit(e);
                      }}
                      noValidate
                    >
                      {/* Name Field */}
                      <div className="flex flex-col w-full">
                        <Input
                          isRequired
                          label={userType === 'agency' ? t('auth.agencyName') : t('auth.name')}
                          name="firstName"
                          placeholder={
                            userType === 'agency'
                              ? t('auth.enterAgencyName')
                              : t('auth.enterYourName')
                          }
                          variant="bordered"
                          value={values.firstName}
                          onChange={(e) => setFieldValue('firstName', e.target.value)}
                          labelPlacement="inside"
                          className="w-full custom-required-label"
                          classNames={{
                            inputWrapper: 'h-14',
                            input: 'text-black',
                          }}
                        />
                        <ErrorMessage
                          name="firstName"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      {/* Email Field (conditionally render) */}
                      {!(inviteToken && !inviteEmail) && (
                        <div className="flex flex-col w-full">
                          <Input
                            isRequired
                            label={t('labels.email')}
                            name="email"
                            placeholder={t('auth.enterEmail')}
                            type="email"
                            variant="bordered"
                            value={values.email}
                            onChange={(e) => setFieldValue('email', e.target.value)}
                            labelPlacement="inside"
                            className="w-full custom-required-label"
                            classNames={{
                              inputWrapper: 'h-14',
                              input: 'text-black',
                            }}
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      )}

                      {/* Password Field */}
                      <div className="flex flex-col w-full">
                        <Input
                          isRequired
                          endContent={
                            <button type="button" onClick={toggleVisibility}>
                              {isVisible ? (
                                <Icon
                                  className="pointer-events-none text-2xl text-default-400"
                                  icon="solar:eye-closed-linear"
                                />
                              ) : (
                                <Icon
                                  className="pointer-events-none text-2xl text-default-400"
                                  icon="solar:eye-bold"
                                />
                              )}
                            </button>
                          }
                          label={t('auth.password')}
                          name="password"
                          placeholder={t('auth.enterPassword')}
                          type={isVisible ? 'text' : 'password'}
                          variant="bordered"
                          value={values.password}
                          onChange={(e) => setFieldValue('password', e.target.value)}
                          labelPlacement="inside"
                          className="w-full custom-required-label"
                          classNames={{
                            inputWrapper: 'h-14',
                            input: 'text-black',
                          }}
                        />

                        {/* Password Strength Indicator */}
                        <div className="flex gap-1 w-full mt-2">
                          {[1, 2, 3, 4].map((level) => {
                            const currentStrength = calculatePasswordStrength(values.password);
                            const isActive = currentStrength >= level;
                            const strengthClass = getPasswordStrengthClass(currentStrength);
                            const colorClass = isActive ? strengthClass : 'bg-auth-strength-none';

                            return (
                              <div
                                key={level}
                                className={`h-1.5 flex-1 rounded-3.5 password-strength-bar ${colorClass}`}
                              />
                            );
                          })}
                        </div>

                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-red-500 text-xs mt-2"
                        />
                        <p className="text-sm text-auth-muted leading-5 mt-1">
                          {t('auth.passwordHint')}
                        </p>
                      </div>

                      {/* Confirm Password Field */}
                      <div className="flex flex-col w-full">
                        <Input
                          isRequired
                          endContent={
                            <button type="button" onClick={toggleConfirmVisibility}>
                              {isConfirmVisible ? (
                                <Icon
                                  className="pointer-events-none text-2xl text-default-400"
                                  icon="solar:eye-closed-linear"
                                />
                              ) : (
                                <Icon
                                  className="pointer-events-none text-2xl text-default-400"
                                  icon="solar:eye-bold"
                                />
                              )}
                            </button>
                          }
                          label={t('auth.repeatPassword')}
                          name="confirmPassword"
                          placeholder={t('auth.enterPasswordAgain')}
                          type={isConfirmVisible ? 'text' : 'password'}
                          variant="bordered"
                          value={values.confirmPassword}
                          onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                          labelPlacement="inside"
                          className="w-full custom-required-label"
                          classNames={{
                            inputWrapper: 'h-14',
                            input: 'text-black',
                          }}
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      {/* Telegram Field (Marketer only) */}
                      {userType === 'marketer' && (
                        <div className="flex flex-col w-full">
                          <Input
                            isRequired
                            label={t('labels.telegram')}
                            name="telegram"
                            placeholder={t('auth.enterTelegram')}
                            variant="bordered"
                            value={(values as any).telegram || ''}
                            onChange={(e) => setFieldValue('telegram', e.target.value)}
                            labelPlacement="inside"
                            className="w-full custom-required-label"
                            classNames={{
                              inputWrapper: 'h-14',
                              input: 'text-black',
                            }}
                          />
                          <ErrorMessage
                            name="telegram"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      )}

                      {/* Terms Acceptance */}
                      <div className="flex w-full flex-col gap-2 mb-4">
                        <div className="flex w-full items-start flex-wrap gap-1">
                          <Checkbox
                            name="acceptTerms"
                            size="sm"
                            isSelected={values.acceptTerms}
                            className="whitespace-nowrap text-sm"
                            onValueChange={(checked) => {
                              setFieldValue('acceptTerms', checked);
                              setSubmitAttempted(true);

                              if (checked) {
                                setSubmitAttempted(false);
                              }
                            }}
                          >
                            {t('auth.ageAgreement')}
                          </Checkbox>
                          <Link
                            href={`https://onlyu.market/${lang || 'en'}/terms-of-service`}
                            isExternal
                            className="whitespace-nowrap text-sm"
                          >
                            {t('auth.termsOfUse')}
                          </Link>
                        </div>
                      </div>

                      {/* Warning Alert for unchecked terms */}
                      {!values.acceptTerms && submitAttempted && (
                        <AlertBlock
                          type="rounded"
                          data={[
                            { severity: 'error', description: t('auth.validation.termsRequired') },
                          ]}
                          className="mb-4"
                        />
                      )}

                      {/* Submit Button */}
                      <Button
                        className="w-full h-12 text-base mb-2 md:mb-4"
                        color="primary"
                        type="submit"
                        isLoading={isLoading || isSubmitting}
                        disabled={isLoading || isSubmitting}
                        onClick={() => {
                          setSubmitAttempted(true);
                        }}
                      >
                        {isLoading || isSubmitting ? t('auth.registering') : t('auth.register')}
                      </Button>
                    </form>
                  )}
                </Formik>
                {/* User Type Toggle */}
                <div className="flex w-full flex-col gap-2 mb-6">
                  <div
                    onClick={() =>
                      navigate('/partner/sign-up', {
                        state: { userType: 'marketer', skipSelect: true },
                      })
                    }
                    className="user-type-card flex w-full cursor-pointer items-center justify-center h-12 rounded-lg bg-auth-surface p-3 transition-colors hover:bg-auth-surface-hover"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-base font-medium text-auth-ink">
                        {t('auth.registrationForMarketer')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sign-in Link */}
                <div className="w-full flex justify-between">
                  <p className="text-sm leading-[1.125rem]">{t('auth.haveAccount')}&nbsp;</p>
                  <Link href="/client/sign-in" size="sm" className="text-sm leading-[1.125rem]">
                    {t('auth.signIn')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
            <div className="shrink-0">
              <LanguageSwitcher variant="ghost" />
            </div>
            <div
              className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
              style={{
                backgroundImage: `url(${RightImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
